import { AfterViewInit, Component, ElementRef, EventEmitter, inject, OnInit, Output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ArticlesService } from '../../../services/articles.service';
import { ArticleBatchDto } from 'src/app/shared/dto/article-batch.dto';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { showDialog } from 'src/app/shared/utils/utils';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { StatusEnum } from 'src/app/shared/enums/production-order-state-ids.enum';
import { CheckActiveSessionResponseDto } from 'src/app/shared/dto/check-active-session-response.dto';

@Component({
    selector: 'app-modal-scan-raw-material',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NzInputModule,
        NzButtonModule,
        NzIconModule,
        NzToolTipModule,
        NzSelectModule,
        LoaderComponent
    ],
    templateUrl: './modal-scan-raw-material.component.html',
    styleUrl: './modal-scan-raw-material.component.scss'
})
export class ModalScanRawMaterialComponent implements AfterViewInit, OnInit {
    @Output() emitService = new EventEmitter();
    @ViewChild('barcodeInput') barcodeInput!: ElementRef<HTMLTextAreaElement>;

    actualArticleId?: number;
    actualArticleName?: string;
    processStatusId?: number;
    availablePackages: PackagingDto[] = [];
    filteredPackages = signal<PackagingDto[]>([]);
    selectedPackage?: number;
    showOnlyLastPackage: boolean = false;
    articleUseSensors: boolean = false;
    hasExistingRawMaterialUsed: boolean = false;
    changeArticleAllowed: boolean = false;
    isCurrentMaterialConsumed = signal(false);
    activeSessionData?: CheckActiveSessionResponseDto;

    private readonly modalRef = inject(NzModalRef);
    private readonly articlesService = inject(ArticlesService);
    private readonly message = inject(NzMessageService);
    private readonly nzModalService = inject(NzModalService);
    private readonly data = inject(NZ_MODAL_DATA);

    barcodeValue = '';
    scannedArticleBatch = signal<ArticleBatchDto | null>(null);
    isLoading = signal(false);
    searchMessage = signal('');
    searchSuccess = signal(false);
    StatusEnum = StatusEnum;

    ngOnInit() {
        this.actualArticleId = this.data?.actualArticleId;
        this.actualArticleName = this.data?.actualArticleName;
        this.processStatusId = this.data?.processStatusId;
        this.availablePackages = this.data?.availablePackages || [];
        this.showOnlyLastPackage = this.data?.showOnlyLastPackage || false;
        this.articleUseSensors = this.data?.articleUseSensors || false;
        this.hasExistingRawMaterialUsed = this.data?.hasExistingRawMaterialUsed || false;
        this.changeArticleAllowed = this.data?.changeArticleAllowed || false;
        this.activeSessionData = this.data?.activeSessionData;
        
        this.filteredPackages.set(this.showOnlyLastPackage
            ? this.filterPackagesByLastFull(this.availablePackages)
            : this.availablePackages);
    }

    private filterPackagesByLastFull(packages: PackagingDto[]): PackagingDto[] {
        if (!packages?.length) {
            return [];
        }

        // Upper bound: last box physically counted by the precintar sensor (quantity > 0).
        let lastFullIndex = -1;
        packages.forEach((pkg, index) => {
            if (this.isPackageFull(pkg)) {
                lastFullIndex = index;
            }
        });

        // Upper bound slice end (exclusive)
        const upperBound = lastFullIndex >= 0 ? lastFullIndex + 1 : packages.length;

        // Lower bound: first unprinted package (produced but not labeled yet).
        // Using the first unprinted package as lower bound ensures ALL unprinted boxes of
        // the current coil are selectable, not just the very last assigned one.
        // This fixes the bug where only 1 unprinted package appeared when there were
        // multiple unprinted packages from the current coil (hasRawMaterials=true but
        // labelPrinted=false) before the last-assigned index.
        let firstUnprintedIndex = -1;
        packages.forEach((pkg, index) => {
            if (!pkg.labelPrinted && (this.isPackageFull(pkg) || pkg.hasRawMaterials || (pkg.rawMaterials?.length ?? 0) > 0)) {
                if (firstUnprintedIndex < 0) {
                    firstUnprintedIndex = index;
                }
            }
        });

        if (firstUnprintedIndex >= 0) {
            // Show all unprinted produced packages from the first unprinted one
            // up to the last sensor-counted package (inclusive).
            return packages.slice(firstUnprintedIndex, upperBound);
        }

        // No unprinted packages yet — show all boxes up to the last sensor-filled one.
        if (lastFullIndex < 0) {
            return packages;
        }
        return packages.slice(0, upperBound);
    }

    private isPackageFull(pkg: PackagingDto): boolean {
        if (pkg.quantity == null) {
            return false;
        }
        return pkg.quantity > 0;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.barcodeInput?.nativeElement?.focus();
        }, 100);
    }

    async onBarcodeInput(): Promise<void> {
        const searchTerm = this.barcodeValue.trim();

        if (!searchTerm) {
            return;
        }

        this.isLoading.set(true);

        try {
            const foundArticleBatch: ArticleBatchDto | PackagingDto = await this.articlesService.findArticleBatchByBatchNumberScan(searchTerm, this.actualArticleId);

            if (!foundArticleBatch) {
                    await this.requestLotFromVelneo(searchTerm);
            } else {
                // Verificar si el artículo del lote coincide con el artículo actual
                await this.handleFoundBatch(foundArticleBatch);
            }
        } catch (error: any) {
            // Si el input es numérico, intentar consultar Velneo
            if (/^\d+$/.test(searchTerm)) {
                await this.requestLotFromVelneo(searchTerm);
            } else {
                this.searchMessage.set('Error al buscar el artículo');
                this.searchSuccess.set(false);
                this.scannedArticleBatch.set(null);
                this.message.error(`Error: ${error.message || 'Error desconocido'}`);
            }
        } finally {
            this.isLoading.set(false);
        }
    }

    /**
     * Verifica si el artículo del lote encontrado coincide con el artículo actual.
     * Si no coincide, muestra un diálogo de confirmación para cambiar el artículo.
     */
    private async handleFoundBatch(foundArticleBatch: ArticleBatchDto): Promise<void> {
        if (this.actualArticleId && foundArticleBatch.articleId !== this.actualArticleId) {
            // Si el cambio de artículo está desactivado, bloquear e informar al usuario
            if (!this.changeArticleAllowed) {
                const newArticleName = foundArticleBatch.article?.name || `ID ${foundArticleBatch.articleId}`;
                const currentArticleName = this.actualArticleName || `ID ${this.actualArticleId}`;
                this.message.create('warning', `
                    <div style="font-size: 24px; line-height: 1.8;">
                        <div><strong>Artículo no coincide</strong></div>
                        <div style="margin-top: 25px;">Artículo actual: <strong>${currentArticleName}</strong></div>
                        <div style="margin-top: 25px;">Artículo del lote: <strong>${newArticleName}</strong></div>
                        <div style="margin-top: 25px;">Cambio de artículo <strong>desactivado</strong></div>
                    </div>
                `, { nzDuration: 10000 });
                this.scannedArticleBatch.set(null);
                this.searchMessage.set('El lote pertenece a un artículo diferente. Cambio de artículo desactivado.');
                this.searchSuccess.set(false);
                this.barcodeValue = '';
                setTimeout(() => this.barcodeInput?.nativeElement?.focus(), 100);
                return;
            }

            const newArticleName = foundArticleBatch.article?.name || `ID ${foundArticleBatch.articleId}`;
            const currentArticleName = this.actualArticleName || `ID ${this.actualArticleId}`;

            const htmlContent = `
                <div style="margin: 25px 0;">
                    <p style="margin-bottom: 25px; font-size: 17px; text-align: center;">
                        El lote <strong style="font-size: 18px;">${foundArticleBatch.batchNumber}</strong> pertenece a un artículo diferente.
                    </p>
                    
                    <div style="background-color: #ffebee; border: 5px solid #f44336; border-radius: 12px; padding: 30px; margin-bottom: 25px; box-shadow: 0 4px 8px rgba(244, 67, 54, 0.3);">
                        <div style="color: #c62828; font-weight: bold; margin-bottom: 15px; display: flex; align-items: center; font-size: 20px;">
                            <span style="font-size: 32px; margin-right: 12px;">✖</span>
                            Artículo Actual (se va a quitar)
                        </div>
                        <div style="color: #212121; font-size: 18px; line-height: 1.6; font-weight: 500; padding-left: 44px;">
                            ${currentArticleName}
                        </div>
                    </div>
                    
                    <div style="background-color: #e8f5e9; border: 5px solid #4caf50; border-radius: 12px; padding: 30px; box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);">
                        <div style="color: #2e7d32; font-weight: bold; margin-bottom: 15px; display: flex; align-items: center; font-size: 20px;">
                            <span style="font-size: 32px; margin-right: 12px;">✓</span>
                            Nuevo Artículo (se va a poner)
                        </div>
                        <div style="color: #212121; font-size: 18px; line-height: 1.6; font-weight: 500; padding-left: 44px;">
                            ${newArticleName}
                        </div>
                    </div>
                    
                    <p style="margin-top: 25px; font-size: 17px; font-weight: 600; text-align: center;">
                        ¿Desea cambiar el artículo?
                    </p>
                </div>
            `;

            const confirmed = await showDialog(
                this.nzModalService,
                'Cambio de Artículo',
                htmlContent
            );

            if (confirmed) {
                this.modalRef.close({
                    changeArticle: true,
                    newArticle: foundArticleBatch.article,
                    articleBatch: foundArticleBatch
                });
            } else {
                this.scannedArticleBatch.set(null);
                this.searchMessage.set('');
                this.searchSuccess.set(false);
                this.barcodeValue = '';
                setTimeout(() => this.barcodeInput?.nativeElement?.focus(), 100);
            }
        } else {
            this.searchMessage.set(`Lote encontrado: ${foundArticleBatch.batchNumber}`);
            this.searchSuccess.set(true);
            this.scannedArticleBatch.set(foundArticleBatch);
            this.message.success('Artículo encontrado correctamente');
        }
    }

    /**
     * Solicita un lote/paquete a Velneo cuando no se encuentra en la base de datos local.
     * Muestra un toast informativo, llama al API, y tras la sincronización
     * re-busca el lote en la base de datos local.
     */
    private async requestLotFromVelneo(searchTerm: string): Promise<void> {
        const velneoToastId = this.message.info('No se encuentra el lote/paquete, solicitando a Velneo...', { nzDuration: 0 }).messageId;

        try {
            const velneoResult = await this.articlesService.requestLotFromVelneo(searchTerm);

            if (!velneoResult.success) {
                this.message.remove(velneoToastId);
                this.searchMessage.set(`No se encontró el lote/paquete en Velneo`);
                this.searchSuccess.set(false);
                this.scannedArticleBatch.set(null);
                this.message.error(velneoResult.error || 'No se encontró el lote/paquete en Velneo');
                return;
            }

            this.message.remove(velneoToastId);

            const articleIdsForSearch = Array.from(new Set([
                this.actualArticleId,
                ...(velneoResult.articles?.map(article => article?.id) ?? []),
                velneoResult.article?.id,
            ].filter((articleId): articleId is number => typeof articleId === 'number' && articleId > 0)));

            try {
                let foundArticleBatch: ArticleBatchDto | null = null;

                for (const articleIdForSearch of articleIdsForSearch) {
                    try {
                        foundArticleBatch = await this.articlesService.findArticleBatchByBatchNumberScan(searchTerm, articleIdForSearch);
                        if (foundArticleBatch) {
                            break;
                        }
                    } catch {
                        // Not found for this articleId — try the next one
                        continue;
                    }
                }

                if (!foundArticleBatch) {
                    try {
                        foundArticleBatch = await this.articlesService.findArticleBatchByBatchNumberScan(searchTerm);
                    } catch {
                        // Not found without articleId filter either
                    }
                }

                if (foundArticleBatch) {
                    await this.handleFoundBatch(foundArticleBatch);
                } else {
                    this.searchMessage.set('No se encontró el lote tras sincronizar con Velneo');
                    this.searchSuccess.set(false);
                    this.scannedArticleBatch.set(null);
                    this.message.error('No se encontró el lote tras sincronizar con Velneo');
                }
            } catch (error: any) {
                this.searchMessage.set('Error al buscar el lote tras sincronizar con Velneo');
                this.searchSuccess.set(false);
                this.scannedArticleBatch.set(null);
                this.message.error('Error al buscar el lote tras sincronizar con Velneo');
            }
        } catch (error: any) {
            this.message.remove(velneoToastId);
            this.searchMessage.set('Error al solicitar datos a Velneo');
            this.searchSuccess.set(false);
            this.scannedArticleBatch.set(null);
            this.message.error('Error al solicitar datos a Velneo. Tiempo de espera agotado.');
        }
    }

    async handleSubmit(): Promise<void> {
        const articleBatch = this.scannedArticleBatch();

        if (!articleBatch) {
            this.message.warning('Debe escanear un código de barras primero');
            return;
        }

        this.isLoading.set(true);

        try {
            this.modalRef.close({
                articleBatch: articleBatch,
                selectedPackage: this.selectedPackage,
                isCurrentMaterialConsumed: this.isCurrentMaterialConsumed()
            });
        } catch (error: any) {
            this.message.error(`Error al procesar: ${error.message || 'Error desconocido'}`);
            this.isLoading.set(false);
        }
    }

    async requestLastPackage(): Promise<void> {
        if (this.showOnlyLastPackage) {
            if (!this.selectedPackage) {
                this.message.warning('Debe seleccionar un paquete');
                return;
            }
            this.modalRef.close({
                selectedPackage: this.selectedPackage,
                isCurrentMaterialConsumed: this.isCurrentMaterialConsumed()
            });
        } else {
            if (!this.selectedPackage) {
                this.message.warning('Debe seleccionar un paquete');
                return;
            }
            this.message.success('Solicitud de último paquete enviada correctamente');
            console.log('Paquete seleccionado:', this.selectedPackage);
        }
    }

    openManualAddModal(): void {
        this.modalRef.close({ openManualSearch: true });
    }

    handleCancel(): void {
        this.modalRef.close(null);
    }
}

